import {
  scheduleWithTimeout,
  type RealtimeHandoverTimer,
} from './realtimeHandoverTimer.ts';

/** PCM16 mono at 24 kHz, which is what the capture worklet sends. */
const BYTES_PER_SECOND = 48_000;

/** Five minutes is far longer than any turn, and 14 MB the page can hold. */
const BUFFER_CAP_BYTES = 5 * 60 * BYTES_PER_SECOND;

const RETRY_DELAY_MS = 3_000;

export type RealtimeHandoverState =
  | 'idle'
  | 'opening'
  | 'warm_ready'
  | 'releasing'
  | 'promoted';

export interface RealtimeHandoverListener {
  /** Open a second socket with the warm query, or open it again after a refusal. */
  openWarm: () => void;
  /** Send these chunks to the warm socket so its model hears the turn in progress. */
  replay: (chunks: Uint8Array<ArrayBuffer>[]) => void;
  /** Tell the old socket it may release at the next turn boundary. */
  sendHandover: () => void;
  /**
   * Tell the warm socket it is authoritative from now on. `cutBytes` is where
   * the old task's turns end, in the warm socket's own count; absent when the
   * old task named none.
   */
  sendPromote: (cutBytes: number | undefined) => void;
  closeOld: () => void;
}

interface BufferedChunk {
  /** Where this chunk starts in everything the call has sent. */
  offset: number;
  bytes: Uint8Array<ArrayBuffer>;
}

/**
 * The client half of a deploy handover, with no socket and no audio in it.
 *
 * It remembers the microphone audio that is in no turn yet, and it decides when
 * the second socket takes the call over. Each socket counts its audio from
 * zero; this counts the whole call and translates between them.
 */
export class RealtimeHandover {
  private readonly listener: RealtimeHandoverListener;
  private readonly timer: RealtimeHandoverTimer;

  private chunks: BufferedChunk[] = [];
  private bufferedBytes = 0;
  private totalBytes = 0;
  /** Where the live socket's own byte count starts in the call. */
  private liveBase = 0;
  private warmBase = 0;
  /** Where the last turn the live task owns ends, in the call; what follows is the next task's. */
  private ownedUntil = 0;
  private state: RealtimeHandoverState = 'idle';
  private cancelRetry: (() => void) | null = null;

  constructor(
    listener: RealtimeHandoverListener,
    timer: RealtimeHandoverTimer = scheduleWithTimeout,
  ) {
    this.listener = listener;
    this.timer = timer;
  }

  get phase(): RealtimeHandoverState {
    return this.state;
  }

  /** Everything the call has sent, across every socket it has used. */
  get sentBytes(): number {
    return this.totalBytes;
  }

  /** With a warm socket ready, losing the old socket is a promotion, not a drop. */
  get canPromote(): boolean {
    return this.state === 'warm_ready' || this.state === 'releasing';
  }

  /**
   * A freshly opened live socket counts from here. What came before is settled:
   * the task that heard it wrote it or it is gone, so no warm socket gets it again.
   */
  liveOpened(): void {
    this.liveBase = this.totalBytes;
    this.ownedUntil = this.totalBytes;
    this.chunks = [];
    this.bufferedBytes = 0;
    this.clearRetry();
    this.state = 'idle';
  }

  /** Every chunk the live socket has actually sent; the caller tees it. */
  audioChunk(bytes: Uint8Array<ArrayBuffer>): void {
    this.chunks.push({ offset: this.totalBytes, bytes });
    this.totalBytes += bytes.byteLength;
    this.bufferedBytes += bytes.byteLength;

    while (this.bufferedBytes > BUFFER_CAP_BYTES && this.chunks.length > 1) {
      this.dropOldest();
    }
  }

  /**
   * Audio up to `sinceBytes` of the live socket is in a turn, so it need not be
   * replayed; the live task owns the turn, which ends at `endBytes`.
   */
  turnEnded(sinceBytes: number, endBytes?: number): void {
    if (endBytes !== undefined) {
      this.ownedUntil = Math.max(this.ownedUntil, this.liveBase + endBytes);
    }
    this.settledBefore(sinceBytes);
  }

  /**
   * Audio before `sinceBytes` of the live socket holds no turn still to be
   * written, so a new task need not hear it: a long replay makes its turns end late.
   */
  settledBefore(sinceBytes: number): void {
    const since = this.liveBase + sinceBytes;
    while (this.chunks.length > 0) {
      const first = this.chunks[0];
      if (first.offset + first.bytes.byteLength > since) {
        return;
      }
      this.dropOldest();
    }
  }

  /** Promoted counts as a fresh start: a hotfix can follow a release by minutes. */
  prepareRequested(): void {
    if (this.state !== 'idle' && this.state !== 'promoted') {
      return;
    }

    this.state = 'opening';
    this.listener.openWarm();
  }

  warmOpened(): void {
    this.clearRetry();
  }

  /** The warm socket reached a task that is not the newest, or died before ready. */
  warmRefused(): void {
    if (this.state === 'promoted') {
      return;
    }

    this.state = 'opening';
    this.clearRetry();
    this.cancelRetry = this.timer(() => {
      this.cancelRetry = null;
      if (this.state === 'opening') {
        this.listener.openWarm();
      }
    }, RETRY_DELAY_MS);
  }

  /** The replay is the first thing the warm socket hears, so its count starts there. */
  warmReady(): void {
    if (this.state !== 'opening') {
      return;
    }

    this.clearRetry();
    this.state = 'warm_ready';
    this.warmBase = this.chunks[0]?.offset ?? this.totalBytes;
    this.listener.replay(this.chunks.map((chunk) => chunk.bytes));
    this.listener.sendHandover();
    this.state = 'releasing';
  }

  /** The old task's turns end at `cutBytes` of its own socket; one that names none leaves it to the warm task. */
  released(cutBytes?: number): void {
    if (cutBytes === undefined) {
      this.promote(undefined);
      return;
    }
    this.ownedUntil = Math.max(this.ownedUntil, this.liveBase + cutBytes);
    this.promote(this.cutOnWarm());
  }

  /** The line to the old task went before it said so: its turn ends say where its turns end. */
  oldSocketLost(): void {
    this.promote(this.cutOnWarm());
  }

  dispose(): void {
    this.clearRetry();
  }

  private promote(cutBytes: number | undefined): void {
    if (!this.canPromote) {
      return;
    }

    this.state = 'promoted';
    this.clearRetry();
    this.liveBase = this.warmBase;
    this.listener.sendPromote(cutBytes);
    this.listener.closeOld();
  }

  private cutOnWarm(): number {
    return Math.max(0, this.ownedUntil - this.warmBase);
  }

  private dropOldest(): void {
    const dropped = this.chunks.shift();
    if (dropped !== undefined) {
      this.bufferedBytes -= dropped.bytes.byteLength;
    }
  }

  private clearRetry(): void {
    this.cancelRetry?.();
    this.cancelRetry = null;
  }
}
